import * as React from 'react';
import { Copy, Loader2, PhoneCall, Plus, RadioReceiver, RefreshCw, Sparkles } from 'lucide-react';
import { useSecretStatus } from '../../../../hooks/useSecretStatus';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Button } from '../../../../components/ui/button';
import { RadioGroup, RadioGroupItem } from '../../../../components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';
import { Badge } from '../../../../components/ui/badge';
import { useToast } from '../../../../components/ui/toast';
import { useTestCall } from '../../../../hooks/useTestChat';
import {
  useCreateTelnyxConnection,
  useTelnyxConnections,
} from '../../../../hooks/useTelnyxConnections';
import { formatCallableError } from '../../../../lib/callableError';
import {
  TELNYX_INBOUND_WEBHOOK,
  TELNYX_MEDIA_FORK_URL,
  TWILIO_CONVERSATION_RELAY_URL,
  TWILIO_INBOUND_WEBHOOK,
} from '../../../../lib/voiceAgent';
import type { FormState } from '../state';
import { TwilioSetupHelp } from './TwilioSetupHelp';
import {
  ProviderConnectionTestButton,
  TelnyxNumbersBlock,
  TwilioNumbersBlock,
} from '../../sections/NumberShopSection';

interface Props {
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
}

export function ProviderTab({ form, setForm }: Props) {
  const { toast } = useToast();
  const testCall = useTestCall();
  const provider = form.telephonyProvider;

  function setProvider(p: 'twilio' | 'telnyx') {
    setForm((prev) => ({
      ...prev,
      telephonyProvider: p,
      twilio:
        p === 'twilio'
          ? prev.twilio ?? {
              accountSid: '',
              phoneNumber: '',
              conversationRelayUrl: TWILIO_CONVERSATION_RELAY_URL,
            }
          : prev.twilio,
      telnyx:
        p === 'telnyx'
          ? prev.telnyx ?? {
              connectionId: '',
              phoneNumber: '',
              mediaForkUrl: TELNYX_MEDIA_FORK_URL,
            }
          : prev.telnyx,
    }));
  }

  async function copy(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: 'In Zwischenablage kopiert' });
    } catch {
      toast({ title: 'Kopieren fehlgeschlagen', variant: 'destructive' });
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Telefonie-Anbieter</CardTitle>
          <CardDescription>
            Twilio (ConversationRelay, EU-Region IE1) oder Telnyx (Voice AI Media Fork, eigenes
            Carrier-Netz).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <RadioGroup
            value={provider}
            onValueChange={(v) => setProvider(v as 'twilio' | 'telnyx')}
            className="grid gap-3 sm:grid-cols-2"
          >
            <ProviderCard
              value="twilio"
              title="Twilio"
              subtitle="ConversationRelay · p50 ≈ 491 ms · EU IE1"
              current={provider}
            />
            <ProviderCard
              value="telnyx"
              title="Telnyx"
              subtitle="Media Fork · <200 ms · 30+ Länder, günstiger"
              current={provider}
            />
          </RadioGroup>
          <div className="flex justify-end">
            <ProviderConnectionTestButton provider={provider} />
          </div>
        </CardContent>
      </Card>

      <PipelineTransportCard form={form} setForm={setForm} />

      {provider === 'twilio' ? (
        <Card>
          <CardHeader>
            <CardTitle>Twilio-Konfiguration</CardTitle>
            <CardDescription>
              Account SID + Auth Token findest du im Twilio-Konsolen-Dashboard. Auth Token landet
              in Secret Manager — nie in Firestore.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <TwilioSetupHelp />

            <div className="space-y-1.5">
              <Label htmlFor="twilio-webhook" className="text-xs uppercase tracking-wider text-muted-foreground">
                Inbound-Webhook (in Twilio eintragen)
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="twilio-webhook"
                  value={TWILIO_INBOUND_WEBHOOK}
                  readOnly
                  className="font-mono text-xs"
                  onFocus={(e) => e.currentTarget.select()}
                />
                <Button
                  size="icon"
                  variant="outline"
                  onClick={() => copy(TWILIO_INBOUND_WEBHOOK)}
                  aria-label="Webhook-URL kopieren"
                  title="Webhook-URL in Zwischenablage kopieren"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <Field label="Account SID" htmlFor="twilio-sid">
              <Input
                id="twilio-sid"
                value={form.twilio?.accountSid ?? ''}
                placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    twilio: {
                      ...(p.twilio ?? { accountSid: '', phoneNumber: '', conversationRelayUrl: TWILIO_CONVERSATION_RELAY_URL }),
                      accountSid: e.target.value,
                    },
                  }))
                }
              />
            </Field>
            <Field label="Auth Token (wird in Secret Manager gespeichert)" htmlFor="twilio-tok">
              <Input
                id="twilio-tok"
                type="password"
                placeholder="••••••••  (leer lassen = unverändert)"
                onChange={(e) =>
                  setForm((p) => ({ ...p, secrets: { ...(p.secrets ?? {}), twilioAuthToken: e.target.value } }))
                }
              />
            </Field>
            <Field label="Telefonnummer (E.164)" htmlFor="twilio-num">
              <Input
                id="twilio-num"
                value={form.twilio?.phoneNumber ?? ''}
                placeholder="+4915123456789"
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    twilio: {
                      ...(p.twilio ?? { accountSid: '', phoneNumber: '', conversationRelayUrl: TWILIO_CONVERSATION_RELAY_URL }),
                      phoneNumber: e.target.value,
                    },
                  }))
                }
              />
            </Field>
            <Field label="ConversationRelay-URL (informativ — automatisch verwaltet)" htmlFor="twilio-cr">
              <div className="flex gap-2">
                <Input
                  id="twilio-cr"
                  value={form.twilio?.conversationRelayUrl ?? ''}
                  placeholder={TWILIO_CONVERSATION_RELAY_URL}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      twilio: {
                        ...(p.twilio ?? { accountSid: '', phoneNumber: '', conversationRelayUrl: '' }),
                        conversationRelayUrl: e.target.value,
                      },
                    }))
                  }
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() =>
                    setForm((p) => ({
                      ...p,
                      twilio: {
                        ...(p.twilio ?? { accountSid: '', phoneNumber: '', conversationRelayUrl: '' }),
                        conversationRelayUrl: TWILIO_CONVERSATION_RELAY_URL,
                      },
                    }))
                  }
                  title="Default-URL einfügen"
                >
                  <Sparkles className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => copy(form.twilio?.conversationRelayUrl ?? TWILIO_CONVERSATION_RELAY_URL)}
                  aria-label="URL kopieren"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                Diese URL <strong>musst du nirgendwo manuell eintragen</strong>. Twilio erhält
                sie automatisch in der TwiML-Antwort, sobald ein Anruf eingeht. Sie steht hier
                nur zur Information / falls du auf eine Staging-Variante umleiten willst.
              </p>
            </Field>

            <div className="rounded-md border border-border p-3 space-y-3">
              <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                Zusätzliche Kanäle
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="cursor-pointer">SMS-Versand</Label>
                  <p className="text-[11px] text-muted-foreground">
                    Schickt Bestätigungen via Twilio Messages API.
                  </p>
                </div>
                <input
                  type="checkbox"
                  className="h-4 w-4 cursor-pointer"
                  checked={!!form.twilio?.smsEnabled}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      twilio: { ...(p.twilio ?? { accountSid: '', phoneNumber: '', conversationRelayUrl: TWILIO_CONVERSATION_RELAY_URL }), smsEnabled: e.target.checked },
                    }))
                  }
                />
              </div>
              {form.twilio?.smsEnabled && (
                <Field label="Messaging-Service-SID (optional)" htmlFor="twilio-msid">
                  <Input
                    id="twilio-msid"
                    value={form.twilio?.messagingServiceSid ?? ''}
                    placeholder="MGxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        twilio: { ...(p.twilio ?? { accountSid: '', phoneNumber: '', conversationRelayUrl: TWILIO_CONVERSATION_RELAY_URL }), messagingServiceSid: e.target.value },
                      }))
                    }
                  />
                </Field>
              )}
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="cursor-pointer">WhatsApp-Versand</Label>
                  <p className="text-[11px] text-muted-foreground">
                    Erfordert verifizierten WhatsApp-Sender im Twilio-Konto.
                  </p>
                </div>
                <input
                  type="checkbox"
                  className="h-4 w-4 cursor-pointer"
                  checked={!!form.twilio?.whatsappEnabled}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      twilio: { ...(p.twilio ?? { accountSid: '', phoneNumber: '', conversationRelayUrl: TWILIO_CONVERSATION_RELAY_URL }), whatsappEnabled: e.target.checked },
                    }))
                  }
                />
              </div>
              {form.twilio?.whatsappEnabled && (
                <Field label="WhatsApp-Sender (E.164 oder whatsapp:+...)" htmlFor="twilio-wa">
                  <Input
                    id="twilio-wa"
                    value={form.twilio?.whatsappFrom ?? ''}
                    placeholder="+14155238886 (Twilio-Sandbox) oder eigene Nummer"
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        twilio: { ...(p.twilio ?? { accountSid: '', phoneNumber: '', conversationRelayUrl: TWILIO_CONVERSATION_RELAY_URL }), whatsappFrom: e.target.value },
                      }))
                    }
                  />
                </Field>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Telnyx-Konfiguration</CardTitle>
            <CardDescription>
              API-Key landet in Secret Manager. Connection-ID + Number-ID findest du in deinem
              Telnyx-Portal.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="telnyx-webhook" className="text-xs uppercase tracking-wider text-muted-foreground">
                Inbound-Webhook (in Telnyx eintragen)
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="telnyx-webhook"
                  value={TELNYX_INBOUND_WEBHOOK}
                  readOnly
                  className="font-mono text-xs"
                  onFocus={(e) => e.currentTarget.select()}
                />
                <Button
                  size="icon"
                  variant="outline"
                  onClick={() => copy(TELNYX_INBOUND_WEBHOOK)}
                  aria-label="Webhook-URL kopieren"
                  title="Webhook-URL in Zwischenablage kopieren"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <TelnyxConnectionPicker form={form} setForm={setForm} />
            <Field label="API-Key (Secret Manager)" htmlFor="telnyx-key">
              <Input
                id="telnyx-key"
                type="password"
                placeholder="••••••••  (leer lassen = unverändert)"
                onChange={(e) =>
                  setForm((p) => ({ ...p, secrets: { ...(p.secrets ?? {}), telnyxApiKey: e.target.value } }))
                }
              />
            </Field>
            <Field label="Telefonnummer (E.164)" htmlFor="telnyx-num">
              <Input
                id="telnyx-num"
                value={form.telnyx?.phoneNumber ?? ''}
                placeholder="+4915123456789"
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    telnyx: {
                      ...(p.telnyx ?? { connectionId: '', phoneNumber: '', mediaForkUrl: TELNYX_MEDIA_FORK_URL }),
                      phoneNumber: e.target.value,
                    },
                  }))
                }
              />
            </Field>
            <Field label="Media-Fork-URL (informativ — automatisch verwaltet)" htmlFor="telnyx-fork">
              <div className="flex gap-2">
                <Input
                  id="telnyx-fork"
                  value={form.telnyx?.mediaForkUrl ?? ''}
                  placeholder={TELNYX_MEDIA_FORK_URL}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      telnyx: {
                        ...(p.telnyx ?? { connectionId: '', phoneNumber: '', mediaForkUrl: '' }),
                        mediaForkUrl: e.target.value,
                      },
                    }))
                  }
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() =>
                    setForm((p) => ({
                      ...p,
                      telnyx: {
                        ...(p.telnyx ?? { connectionId: '', phoneNumber: '', mediaForkUrl: '' }),
                        mediaForkUrl: TELNYX_MEDIA_FORK_URL,
                      },
                    }))
                  }
                  title="Default-URL einfügen"
                >
                  <Sparkles className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => copy(form.telnyx?.mediaForkUrl ?? TELNYX_MEDIA_FORK_URL)}
                  aria-label="URL kopieren"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </Field>

            <div className="rounded-md border border-border p-3 space-y-3">
              <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                Zusätzliche Kanäle
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="cursor-pointer">SMS-Versand</Label>
                  <p className="text-[11px] text-muted-foreground">
                    Schickt Bestätigungen via Telnyx Messages API.
                  </p>
                </div>
                <input
                  type="checkbox"
                  className="h-4 w-4 cursor-pointer"
                  checked={!!form.telnyx?.smsEnabled}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      telnyx: { ...(p.telnyx ?? { connectionId: '', phoneNumber: '', mediaForkUrl: TELNYX_MEDIA_FORK_URL }), smsEnabled: e.target.checked },
                    }))
                  }
                />
              </div>
              {form.telnyx?.smsEnabled && (
                <Field label="Messaging-Profile-ID (optional)" htmlFor="telnyx-mpid">
                  <Input
                    id="telnyx-mpid"
                    value={form.telnyx?.messagingProfileId ?? ''}
                    placeholder="40017ed0-…"
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        telnyx: { ...(p.telnyx ?? { connectionId: '', phoneNumber: '', mediaForkUrl: TELNYX_MEDIA_FORK_URL }), messagingProfileId: e.target.value },
                      }))
                    }
                  />
                </Field>
              )}
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="cursor-pointer">WhatsApp-Versand</Label>
                  <p className="text-[11px] text-muted-foreground">
                    Erfordert WhatsApp-Profil im Telnyx-Mission-Control.
                  </p>
                </div>
                <input
                  type="checkbox"
                  className="h-4 w-4 cursor-pointer"
                  checked={!!form.telnyx?.whatsappEnabled}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      telnyx: { ...(p.telnyx ?? { connectionId: '', phoneNumber: '', mediaForkUrl: TELNYX_MEDIA_FORK_URL }), whatsappEnabled: e.target.checked },
                    }))
                  }
                />
              </div>
              {form.telnyx?.whatsappEnabled && (
                <Field label="WhatsApp-Sender (E.164)" htmlFor="telnyx-wa">
                  <Input
                    id="telnyx-wa"
                    value={form.telnyx?.whatsappFrom ?? ''}
                    placeholder="+4912345678"
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        telnyx: { ...(p.telnyx ?? { connectionId: '', phoneNumber: '', mediaForkUrl: TELNYX_MEDIA_FORK_URL }), whatsappFrom: e.target.value },
                      }))
                    }
                  />
                </Field>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Number purchase + active inventory for the currently-selected
          provider. Inlined here so the user sees one continuous flow:
          1) pick provider, 2) configure credentials, 3) buy a number,
          4) review what's already on the account. */}
      {provider === 'twilio' ? <TwilioNumbersBlock /> : <TelnyxNumbersBlock />}

      {/* TODO Brief Section 9 — Test-Anruf wird in einer eigenen Iteration
          überarbeitet: Pipeline-Simulator (ohne echte Telefonkosten,
          STT→LLM→TTS-Trace mit Latenz-Aufschlüsselung) plus echter
          Live-Test über WebRTC im Browser bzw. Deep-Link in die Mobile-App.
          Bis dahin bleibt der existierende Outbound-Call-Trigger erhalten. */}
      <Card>
        <CardHeader>
          <CardTitle>Test-Anruf</CardTitle>
          <CardDescription>
            Initiiert einen Outbound-Call von der konfigurierten Nummer mit einer Test-Sprachausgabe.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            onClick={async () => {
              try {
                const r = await testCall.mutateAsync({});
                toast({
                  title: 'Test-Anruf gestartet',
                  description: `Status: ${r.status ?? '–'} (SID ${r.sid ?? '–'})`,
                });
              } catch (err) {
                toast({
                  title: 'Test-Anruf fehlgeschlagen',
                  description: (err as Error).message,
                  variant: 'destructive',
                });
              }
            }}
            disabled={testCall.isPending}
            variant="outline"
          >
            <PhoneCall className="mr-2 h-4 w-4" />
            {testCall.isPending ? 'Wähle …' : 'Test-Anruf starten'}
          </Button>
          {provider === 'telnyx' && (
            <p className="mt-2 text-xs text-muted-foreground">
              Hinweis: Test-Anrufe für Telnyx werden in einer späteren Iteration ausgebaut.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
    </div>
  );
}

/**
 * Telnyx Connection-ID picker: lists existing TeXML applications via
 * the API, lets the user select one or create a new one wired to the
 * CP-3 voice-agent webhook. Falls back to a plain text input when the
 * API key is missing or the listing fails.
 */
function TelnyxConnectionPicker({
  form,
  setForm,
}: {
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
}) {
  const { toast } = useToast();
  // We only try to fetch when the user has at least the placeholder telnyx
  // block — otherwise the API call will be wasted on a 401.
  const enabled = !!form.telnyx;
  const list = useTelnyxConnections(enabled);
  const create = useCreateTelnyxConnection();
  const [manual, setManual] = React.useState(false);

  const currentId = form.telnyx?.connectionId ?? '';
  const items = list.data ?? [];
  // If the saved id doesn't match anything we fetched, allow the user to
  // type freely instead of forcing them to recreate.
  const idIsKnown = !currentId || items.some((c) => c.id === currentId);

  function setConnectionId(id: string) {
    setForm((p) => ({
      ...p,
      telnyx: {
        ...(p.telnyx ?? {
          connectionId: '',
          phoneNumber: '',
          mediaForkUrl: TELNYX_MEDIA_FORK_URL,
        }),
        connectionId: id,
      },
    }));
  }

  async function onCreate() {
    try {
      const r = await create.mutateAsync();
      setConnectionId(r.id);
      toast({
        title: 'Voice-Connection erstellt',
        description: `${r.friendlyName} → ${r.id.slice(0, 12)}…`,
      });
    } catch (err) {
      toast({
        title: 'Erstellen fehlgeschlagen',
        description: formatCallableError(err),
        variant: 'destructive',
      });
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex items-end justify-between gap-2">
        <Label htmlFor="telnyx-conn">Voice-Connection</Label>
        <div className="flex items-center gap-1">
          {!manual && (
            <>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-7 text-xs"
                onClick={() => list.refetch()}
                disabled={list.isFetching || !enabled}
                title="Aus Telnyx neu laden"
              >
                <RefreshCw
                  className={['mr-1 size-3', list.isFetching ? 'animate-spin' : ''].join(' ')}
                />
                Aktualisieren
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-7 text-xs"
                onClick={() => setManual(true)}
              >
                Manuell eingeben
              </Button>
            </>
          )}
          {manual && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-7 text-xs"
              onClick={() => setManual(false)}
            >
              Aus Liste wählen
            </Button>
          )}
        </div>
      </div>

      {manual ? (
        <Input
          id="telnyx-conn"
          value={currentId}
          placeholder="z. B. 1234567890123456789"
          onChange={(e) => setConnectionId(e.target.value.trim())}
        />
      ) : list.isLoading ? (
        <div className="flex h-10 items-center gap-2 rounded-md border border-border bg-muted/20 px-3 text-sm text-muted-foreground">
          <Loader2 className="size-3.5 animate-spin" /> Lade Connections aus Telnyx …
        </div>
      ) : list.error ? (
        <div className="space-y-2">
          <div className="rounded-md bg-destructive/10 p-3 text-xs text-destructive">
            {formatCallableError(list.error)}
          </div>
          <p className="text-xs text-muted-foreground">
            Tipp: Erst den Telnyx-API-Key im Reiter „API-Schlüssel" speichern, dann kannst du
            hier deine Connections aus Telnyx auswählen.
          </p>
          <Input
            id="telnyx-conn"
            value={currentId}
            placeholder="Connection-ID manuell eingeben"
            onChange={(e) => setConnectionId(e.target.value.trim())}
          />
        </div>
      ) : items.length === 0 ? (
        <div className="space-y-2 rounded-md border border-dashed border-border p-3">
          <div className="text-sm text-muted-foreground">
            Noch keine TeXML-Connection in deinem Telnyx-Account.
          </div>
          <Button
            type="button"
            size="sm"
            onClick={() => void onCreate()}
            disabled={create.isPending}
          >
            {create.isPending ? (
              <Loader2 className="mr-2 size-3.5 animate-spin" />
            ) : (
              <Plus className="mr-2 size-3.5" />
            )}
            Voice-Connection automatisch erstellen
          </Button>
          <p className="text-xs text-muted-foreground">
            Erzeugt eine TeXML-Application mit Voice-URL{' '}
            <code className="font-mono">{TELNYX_INBOUND_WEBHOOK}</code> und übernimmt die ID hier.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          <Select
            value={idIsKnown ? currentId : '__custom__'}
            onValueChange={(v) => {
              if (v === '__custom__') return;
              setConnectionId(v);
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="— Connection wählen —" />
            </SelectTrigger>
            <SelectContent>
              {items.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  <span className="flex items-center gap-2">
                    <span className="font-medium">{c.friendlyName || 'unbenannt'}</span>
                    <span className="font-mono text-xs text-muted-foreground">
                      {c.id.slice(0, 12)}…
                    </span>
                    {c.ownedByCp3 && <Badge variant="success">CP-3</Badge>}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!idIsKnown && currentId && (
            <p className="text-xs text-amber-300">
              Aktuell hinterlegte ID <code>{currentId}</code> ist in deinem Telnyx-Account nicht
              (mehr) sichtbar. Wähle eine andere oder erstelle eine neue.
            </p>
          )}
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => void onCreate()}
            disabled={create.isPending}
          >
            {create.isPending ? (
              <Loader2 className="mr-2 size-3.5 animate-spin" />
            ) : (
              <Plus className="mr-2 size-3.5" />
            )}
            Neue CP-3-Connection erstellen
          </Button>
        </div>
      )}
    </div>
  );
}

function ProviderCard({
  value,
  title,
  subtitle,
  current,
}: {
  value: string;
  title: string;
  subtitle: string;
  current: string;
}) {
  const checked = value === current;
  return (
    <Label
      htmlFor={`prov-${value}`}
      className={[
        'flex cursor-pointer items-start gap-3 rounded-md border p-4',
        checked ? 'border-primary bg-primary/5' : 'border-border',
      ].join(' ')}
    >
      <RadioGroupItem id={`prov-${value}`} value={value} className="mt-1" />
      <div>
        <div className="font-medium">{title}</div>
        <div className="text-xs text-muted-foreground">{subtitle}</div>
      </div>
    </Label>
  );
}


// ── Pipeline-Transport ──────────────────────────────────────────────
//
// Wählt, *wie* die Audio-Daten zwischen Telefon und Voice-Agent fließen:
//
//   • twilio-media : klassisch — Twilio ConversationRelay WebSocket
//                    (Default, μ-law 8 kHz)
//   • telnyx-media : Telnyx Media Fork (gleicher Wire-Format)
//   • livekit      : Add-on — LiveKit SIP-Inbound Trunk + WebRTC
//                    Opus 48 kHz. Erfordert LK-Credentials im
//                    API-Schlüssel-Reiter.
//
// Die Auswahl wirkt fürs eingehende Telefon-Audio aller Nummern des
// Users. Der Browser-Test-Chat hat einen eigenen Toggle (in /settings/test).

function PipelineTransportCard({
  form,
  setForm,
}: {
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
}) {
  const status = useSecretStatus();
  const lk = status.data?.livekit;
  const livekitReady = !!form.livekitUrl && !!lk?.apiKey && !!lk?.apiSecret;
  const current = form.pipelineTransport ?? "twilio-media";

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <RadioReceiver className="h-5 w-5 text-primary" />
          Pipeline-Transport
        </CardTitle>
        <CardDescription>
          Bestimmt, wie Audio zwischen Telefon-Anbieter und Voice-Agent
          fließt. Standard nutzt den klassischen Twilio/Telnyx Media-Stream-
          Pfad. „LiveKit" routet alle Anrufe via SIP-Trunk durch dein
          LiveKit-Projekt (HD-Audio in Opus 48 kHz).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <RadioGroup
          value={current}
          onValueChange={(v) =>
            setForm((p) => ({
              ...p,
              pipelineTransport: v as
                | "twilio-media"
                | "telnyx-media"
                | "livekit",
            }))
          }
          className="grid gap-3 sm:grid-cols-3"
        >
          <TransportCard
            value="twilio-media"
            current={current}
            title="Twilio Media"
            subtitle="μ-law 8 kHz · stabil"
          />
          <TransportCard
            value="telnyx-media"
            current={current}
            title="Telnyx Media"
            subtitle="μ-law 8 kHz · 30+ Länder"
          />
          <TransportCard
            value="livekit"
            current={current}
            title="LiveKit"
            subtitle={
              livekitReady
                ? "Opus 48 kHz · HD-Audio"
                : "Credentials fehlen — API-Schlüssel-Reiter"
            }
            disabled={!livekitReady}
          />
        </RadioGroup>
        {current === "livekit" && (
          <p className="text-xs text-muted-foreground">
            Damit Anrufe via LiveKit ankommen, musst du in Twilio (oder
            Telnyx) einen Elastic SIP Trunk mit Origination URI auf deinen
            LK-Inbound-Trunk konfigurieren. Die SIP-URI findest du im
            API-Schlüssel-Reiter → LiveKit nach erfolgreichem
            Verbindungs-Test.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function TransportCard({
  value,
  current,
  title,
  subtitle,
  disabled,
}: {
  value: "twilio-media" | "telnyx-media" | "livekit";
  current: string;
  title: string;
  subtitle: string;
  disabled?: boolean;
}) {
  return (
    <Label
      htmlFor={`tp-${value}`}
      className={[
        "flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors",
        current === value
          ? "border-primary bg-primary/5"
          : "border-border hover:border-primary/40",
        disabled && "pointer-events-none opacity-50",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <RadioGroupItem
        id={`tp-${value}`}
        value={value}
        className="mt-1"
        disabled={disabled}
      />
      <div>
        <div className="font-medium">{title}</div>
        <div className="text-xs text-muted-foreground">{subtitle}</div>
      </div>
    </Label>
  );
}
